"use client";

import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ArrowRight, Info } from "lucide-react";
import { SafeImage } from "@/components/common/SafeImage";
import { formatPrice, getDisplayPrice } from "@/services/pricingService";
import { resolveProductImages } from "@/utils/media";
import { useAuth } from "@/context/AuthContext";

const OriginalOemReference = ({ oemProduct }) => {
  const { user } = useAuth();
  if (!oemProduct) return null;

  const imageUrl = resolveProductImages(oemProduct.images || [])[0] || "/placeholder.jpg";
  const stockQty = oemProduct.stockQty ?? 0;
  const inStock = stockQty > 0;
  const pricing = getDisplayPrice(oemProduct, user);

  return (
    <div className="my-10 animate-fade-in">
      <div className="bg-blue-50/50 border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
        <div className="p-4 bg-blue-600 flex items-center gap-2 text-white">
          <ShieldCheck className="w-5 h-5" />
          <h2 className="text-sm font-bold uppercase tracking-wider">Original OEM Reference</h2>
        </div>
        
        <div className="p-5 md:p-6 flex flex-col md:flex-row gap-6">
          {/* Image */}
          <div className="relative w-full md:w-48 aspect-square rounded-xl overflow-hidden bg-white border border-blue-100/50 shrink-0 shadow-inner">
            <SafeImage
              src={imageUrl}
              alt={oemProduct.name}
              fill
              className="object-contain p-2"
            />
            <div className="absolute top-2 left-2">
              <Badge className="bg-blue-600 text-white border-none text-[10px]">OEM</Badge>
            </div>
          </div>

          {/* Details */}
          <div className="flex-1 flex flex-col justify-between py-1">
            <div>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h3 className="text-lg md:text-xl font-bold text-slate-900 leading-tight">
                  {oemProduct.name}
                </h3>
                <div className="flex flex-col items-end">
                  <span className="text-lg font-bold text-blue-700 whitespace-nowrap">
                    {formatPrice(pricing.price)}
                  </span>
                  {pricing.originalPrice && (
                    <span className="text-xs text-slate-400 line-through">
                      {formatPrice(pricing.originalPrice)}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-6 mb-4">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Part Number</span>
                  <span className="text-sm font-mono font-medium text-slate-700">{oemProduct.oemNumber || "N/A"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Vehicle Brand</span>
                  <span className="text-sm font-medium text-slate-700">{oemProduct.vehicleBrand || "N/A"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Availability</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${inStock ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                    <span className={`text-xs font-bold ${inStock ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {inStock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">SKU</span>
                  <span className="text-xs font-mono text-slate-500">{oemProduct.sku || "N/A"}</span>
                </div>
              </div>
              
              <div className="p-3 bg-white/60 border border-blue-100/50 rounded-xl flex items-start gap-2.5 mb-6 md:mb-0">
                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-600 leading-relaxed italic">
                  This product is linked to the original OEM reference shown above.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 mt-4">
              <Button asChild className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-11 px-6 font-bold shadow-md shadow-blue-200">
                <Link href={`/product/${oemProduct.slug}`} className="w-full sm:w-auto">
                  View Original OEM Part
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OriginalOemReference;
